import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatCurrency } from '@/lib/format'
import type { ProjectInvoice } from '@/types'

interface InvoiceSelectFieldProps {
  invoices: ProjectInvoice[]
  value: string | undefined
  onChange: (id: string) => void
  emptyAction?: { label: string; onClick: () => void }
}

const TYPE_LABEL: Record<ProjectInvoice['type'], string> = {
  deposit: 'ACOMPTE',
  final: 'FINALE',
  standalone: 'STANDALONE',
  credit_note: 'NC',
}

/**
 * Dropdown of the project's invoices that can still be credit-noted —
 * excludes credit_notes and any invoice already marked superseded. Empty state
 * surfaces a CTA (typically: "Note de crédit libre") so Élodie isn't stuck.
 * SOLY-CN-001 mini-scope 04.
 */
export function InvoiceSelectField({ invoices, value, onChange, emptyAction }: InvoiceSelectFieldProps) {
  const annullable = invoices.filter((i) => i.type !== 'credit_note' && !i.isSuperseded)

  if (annullable.length === 0) {
    return (
      <div
        data-testid="credit-note-invoice-select-empty"
        className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground"
      >
        <p>Aucune facture annulable pour ce projet.</p>
        {emptyAction ? (
          <button
            type="button"
            data-testid="credit-note-empty-state-cta"
            onClick={emptyAction.onClick}
            className="mt-2 text-primary underline-offset-2 hover:underline"
          >
            {emptyAction.label}
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="credit-note-invoice-select" className="w-full">
        <SelectValue placeholder="Sélectionner la facture à annuler" />
      </SelectTrigger>
      <SelectContent>
        {annullable.map((inv) => (
          <SelectItem
            key={inv.id}
            value={inv.id}
            aria-label={`Facture ${TYPE_LABEL[inv.type]} numéro ${inv.number} du ${new Date(inv.invoiceDate).toLocaleDateString('fr-BE')}, ${inv.amountWithTax} euros TTC`}
          >
            <span className="font-mono text-xs text-muted-foreground mr-2">{TYPE_LABEL[inv.type]}</span>
            <span className="font-medium">{inv.number}</span>
            <span className="text-muted-foreground mx-2">·</span>
            <span className="text-sm">{new Date(inv.invoiceDate).toLocaleDateString('fr-BE')}</span>
            <span className="text-muted-foreground mx-2">·</span>
            <span className="font-medium">{formatCurrency(inv.amountWithTax)}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
